<template>
  <div class="middle-panel">
    <q2-action-group full-width=true orientation="horizontal">
        <q2-btn
            :intent="selectedPlatform === 'all' ? 'workflow-primary' : 'neutral'"
            @click="selectedPlatform = 'all'"
        >
            All
        </q2-btn>
        <q2-btn
            :intent="selectedPlatform === 'ios' ? 'workflow-primary' : 'neutral'"
            @click="selectedPlatform = 'ios'"
        >
            iOS
        </q2-btn>
        <q2-btn
            :intent="
            selectedPlatform === 'android' ? 'workflow-primary' : 'neutral'
            "
            @click="selectedPlatform = 'android'"
        >
            Android
        </q2-btn>
    </q2-action-group>
    <q2-data-table
      class="middle-table"
      :headers.prop="headerData"
      :rows.prop="buildRows"
      empty-icon="inbox"
      empty-message="No builds found"
      bordered="horizontal"
      clickable="true"
      hide-clickable="true"

      @click="onRowClick"
    >
      <!-- q2-data-table renders rows in its shadow DOM and exposes no per-row
           class or ::part, so a row cannot be tinted from out here. What it does
           expose is a `row-<id>-cell-<key>` slot per cell: content placed there
           lives in our light DOM, where scoped CSS reaches it. Overriding every
           cell of a marked build is therefore how we tint the whole row. -->
      <template v-for="row in pendingDeletionRows" :key="row.id">
        <div
          v-for="header in headerData"
          :key="header.key"
          :slot="`row-${row.id}-cell-${header.key}`"
          class="pending-deletion-cell"
          :title="pendingDeletionTitle(row)"
        >
          {{ row.cells[header.key].value }}
        </div>
      </template>
    </q2-data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { FiBuild } from "../types/build";

const headerData = [
  { title: "Timestamp", key: "timestamp" },
  { title: "Version", key: "container-version" },
  { title: "Platform", key: "platform" },
];

interface Props {
  selectedConfig: string | null;
  selectedBuildId: string | null;
  buildData: FiBuild[];
  platform?: Platform;
}

interface Row {
  id: string;
  configId: string;
  selected: boolean;
  cells: {
    timestamp: { value: string };
    "container-version": { value: string };
    platform: { value: string }; // 👈 instead of just `string`
    [key: string]: { value: string };
  };
  buildId: string;
  markedForDeletion?: string | null;
}

// eslint-disable-next-line vue/valid-define-props
const props = defineProps<Props>();

type Platform = "android" | "ios" | "all";

function isPlatform(value: string): value is Platform {
  return value === "all" || value === "ios" || value === "android";
}

const selectedPlatform = ref<Platform>(
  isPlatform(props.platform || "") ? props.platform : "all"
);

function formatTimestamp(isoString: string | null | undefined): string {
  if (!isoString) return "N/A";
  const date = new Date(isoString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  const parts = new Intl.DateTimeFormat(undefined, options).formatToParts(date);
  const lookup: { [key: string]: string } = {};
  for (const part of parts) {
    if (part.type !== "literal") {
      lookup[part.type] = part.value;
    }
  }
  return `${lookup.month} ${lookup.day}, ${lookup.year} ${lookup.hour}:${
    lookup.minute
  } ${lookup.dayPeriod || ""}`.trim();
}

// const selectedRowId = ref<string | null>(null);

const filteredBuilds = computed(() => {
  const builds = props.buildData;
  const platform = selectedPlatform.value;

  return builds.filter((row) => {
    const platformValue =
      typeof row.platform === "string"
        ? row.platform.toLowerCase()
        : "unknown";

    const matchesPlatform =
      selectedPlatform.value === "all" ||
      platformValue === platform;

    return matchesPlatform;
  });
});

const buildRows = computed(() => {
  const builds = filteredBuilds.value;

  const rows: Row[] = [];

  builds.forEach((build) => {
      rows.push({
        id: build.buildId,
        selected: build.buildId == props.selectedBuildId,
        configId: build.configId,
        sortTime: build.createdAt,
        cells: {
          timestamp: {
            value: formatTimestamp(build.createdAt) || "N/A",
          },
          "container-version": {
            value: build.buildSettings.containerVersion || "N/A",
          },
          platform: { value: build.platform },
        },
        buildId: build.buildId,
        markedForDeletion: build.markedForDeletion,
      });
  });

  return rows;
});

const pendingDeletionRows = computed(() =>
  buildRows.value.filter((row) => row.markedForDeletion != null)
);

// The TTL index purges the document 10 days after it is stamped, so the tint has
// a deadline worth surfacing — the row is red for a window, not forever.
const TTL_DAYS = 10;

function pendingDeletionTitle(row: Row): string {
  const markedAt = new Date(row.markedForDeletion as string);
  if (Number.isNaN(markedAt.getTime())) return "Pending deletion";

  const purgeAt = new Date(markedAt);
  purgeAt.setDate(purgeAt.getDate() + TTL_DAYS);

  return `Pending deletion — permanently removed ${formatTimestamp(
    purgeAt.toISOString()
  )} unless restored`;
}

const emit = defineEmits<{
  (e: "selected-build-id", id: string): void;
  (e: "platform-selected", platform: string): void;
}>();

watch(selectedPlatform, (platform) => {
  emit("platform-selected", platform);

  // Check if currently selected row exists in the filtered platform results
  const selectedRowExists = buildRows.value.some(row => row.id === props.selectedBuildId);

  // If selected row doesn't exist in filtered results, select the first row
  if (!selectedRowExists && buildRows.value.length > 0) {
    emit("selected-build-id", buildRows.value[0].id);
  }
});

watch(
  () => props.platform,
  (platform) => {
    if (platform && isPlatform(platform) && platform !== selectedPlatform.value) {
      selectedPlatform.value = platform;
    }
  }
);

function onRowClick(event: CustomEvent<{ row: Row }>) {
  const row = event.detail?.row;

  if (row?.id) {
    emit("selected-build-id", row?.id);
  }
}
</script>

<style scoped>

.middle-table {
    font-size:10pt;
}

.middle-table {
    --tct-data-table-header-background: #faf7fd;
    --tct-data-table-header-border-color: rgb(216.75, 216.75, 216.75);
    --tct-data-table-clickable-row-hover-background: #F8F8F8;
    --tct-data-table-selected-row-background: #f6f6f6;
    --tct-data-table-row-border-color: rgb(216.75, 216.75, 216.75);
    /* Pinned so .pending-deletion-cell below can cancel it exactly. 15px is what
       the table already resolved to (Tecton's --app-scale-3x), so this changes no
       existing spacing. */
    --tct-data-table-cell-padding: 15px;
    color: #404040;
}

/* Slotted cell content sits inside the table's <td>, so on its own a background
   would stop short of the cell padding and read as a highlight behind the text
   rather than a tinted row. Cancelling that padding with a negative margin and
   re-applying it as our own padding makes the tint fill the cell edge to edge.
   The offset is written as a literal rather than calc() over the custom property
   because that property accepts a multi-value shorthand, which calc() cannot
   negate. */
.middle-table .pending-deletion-cell {
    display: block;
    margin: -15px;
    padding: 15px;
    background: #fdecec;
    color: #a3221b;
}

.middle-panel {
    --tct-btn-primary-background: #594bab;
    --tct-btn-neutral-background: #faf7fd;
    --tct-btn-primary-box-shadow: none;
    --tct-btn-neutral-box-shadow: none;
    --tct-btn-border-radius: 5px;
}

</style>
